import { cn } from '@heidi/utils';
import { cva } from 'class-variance-authority';
import { createContext, useContext, type ComponentType, type ReactNode } from 'react';

import {
  DENSITIES,
  DENSITY_SPACING,
  DENSITY_TYPE,
  HEADING_LEVELS,
  type ProseDensity,
  type ProseHeadingLevel,
} from './response-scale';
import { createTableMdComponents } from './response-table';

export { createTableMdComponents };

/**
 * The single typography + spacing system for streamed assistant prose.
 *
 * Every surface that renders a markdown answer — Evidence (in-session panel and
 * the out-of-session document column), Work (home, project chat, the in-session
 * Work panel), the coding Ask Heidi thread, the shared-session document tabs and
 * the desktop overlay — builds its Streamdown `components` map from here. Before
 * this module those surfaces carried three independent scales: a 14px Evidence
 * in-session baseline, a 16px Evidence out-of-session override layer applied from
 * an ancestor, and a 14px Work wrapper with no heading map at all. The same
 * conversation could show two body sizes when a Work transcript embedded an
 * Evidence answer.
 *
 * ## One typescale, two heading tiers
 *
 * Body copy is `text-base` at `comfortable` and `text-sm` at `compact`. Six
 * markdown levels collapse onto TWO visual tiers, which differ by font family
 * rather than by size:
 *
 * | tier    | levels | font     | weight   | size      | leading |
 * | ------- | ------ | -------- | -------- | --------- | ------- |
 * | Section | h1, h2 | Exposure | semibold | 24 / 20px | tight   |
 * | Label   | h3-h6  | UI sans  | semibold | body size | normal  |
 *
 * See the tier rationale on `proseHeading` below, and the level-normalization
 * that feeds it in `response-headings.ts`. The sizes and margins themselves live
 * in `response-scale.ts`; the gaps BETWEEN blocks live in `response-thread.ts`.
 *
 * ## Two densities, chosen by surface role
 *
 * `comfortable` is a primary reading surface (Evidence out-of-session, Work home
 * and project chat, and full-screen mobile). `compact` is a reference panel
 * beside the user's real work (the in-session Evidence sidebar, the in-session
 * Work panel, the desktop overlay).
 *
 * Density is NOT a session-state switch, and it is NOT column width. Width
 * correlates with role but does not define it: full-screen chat on a phone is a
 * narrow column that must stay at 16px, and an Evidence sidebar on a 27" monitor
 * is a wide viewport that must be compact. The surface declares its own role,
 * which it always knows. Both presets share the tier shape and the line-height
 * ladder and differ only in amplitude.
 *
 * ## Why the margins carry `!important`
 *
 * Streamdown composes its wrapper class as `twMerge('space-y-4 whitespace-normal
 * *:first:mt-0 *:last:mb-0', className)`. Passing a `space-y-*` class REPLACES
 * the 16px default (tailwind-merge resolves the group), but it cannot remove the
 * utility altogether — and `space-y-0` still emits
 * `.space-y-0 > :not([hidden]) ~ :not([hidden]) { margin-top: 0 }` at specificity
 * (0,3,0), which outranks a bare `mt-5` (0,1,0). `!important` is the only
 * mechanism a single utility class has to win that, so every block margin here
 * keeps its `!`.
 *
 * What HAS gone is the second layer. The margins used to be set twice — once per
 * element and again from an ancestor via ~79 descendant selectors
 * (`[&_.md-h1]:!mt-12 sm:…`) — and that self-inflicted fight is what needed the
 * `[&_>div>.md-h1:first-child]` specificity hacks to break its own ties. Each
 * element now resolves to exactly one computed class string, and the only
 * selectors left in the wrapper are the genuinely relational ones (adjacent
 * siblings and callout scoping), which no utility class can express.
 */
export type { ProseDensity, ProseHeadingLevel } from './response-scale';

/**
 * Density published by the host that owns the column, for the prose nested
 * somewhere inside it.
 *
 * Context rather than a prop because the surfaces that need `compact` — the
 * in-session Work panel and the desktop overlay — sit five render layers above
 * the component that renders a markdown block, and every layer between them is
 * generic timeline plumbing with no opinion about column width. A component that
 * knows its own density still passes it explicitly; the context is the fallback
 * for the ones that only know they are somewhere inside a narrow panel.
 */
const ProseDensityContext = createContext<ProseDensity | undefined>(undefined);

export const ProseDensityProvider = ({
  density,
  children,
}: {
  density: ProseDensity;
  children: ReactNode;
}) => (
  <ProseDensityContext.Provider value={density}>{children}</ProseDensityContext.Provider>
);

/** The host's density, or `fallback` when no host has published one. */
export const useProseDensity = (fallback: ProseDensity = 'comfortable'): ProseDensity =>
  useContext(ProseDensityContext) ?? fallback;

/**
 * Marker class shared by "callout" blocks in a response (`KeyPointsBlock` and
 * `KeySafetyNotes`). The wrapper scopes the callout's outer gutter and stops the
 * body rhythm leaking into the card; a single marker keeps the two blocks
 * consistent by construction.
 */
export const CALLOUT_BLOCK_MARKER_CLASS = 'callout-block';

/** Props Streamdown passes to a custom element renderer. */
type MdComponentProps = {
  className?: string;
  children?: ReactNode;
  [key: string]: unknown;
};

/** Handler shape for a caller-supplied `<a>` renderer. */
type LinkComponent = ComponentType<{ children?: ReactNode; href?: string }>;

/** A single prose element renderer. */
type ProseElement = ComponentType<MdComponentProps>;

/**
 * The prose map, keyed by markdown element name, ready to spread onto
 * Streamdown's `components` prop.
 *
 * The elements are listed rather than left as an index signature so a consumer
 * that wraps one of them — `useEvidenceAnswerComponents` swaps `p` for a `div`
 * when the paragraph hosts a figure, then delegates to the original — gets a
 * callable component back instead of `T | undefined`.
 */
export type ResponseProseComponents = {
  p: ProseElement;
  h1: ProseElement;
  h2: ProseElement;
  h3: ProseElement;
  h4: ProseElement;
  h5: ProseElement;
  h6: ProseElement;
  ul: ProseElement;
  ol: ProseElement;
  li: ProseElement;
  strong: ProseElement;
  em: ProseElement;
  blockquote: ProseElement;
  hr: ProseElement;
  table: ProseElement;
  thead: ProseElement;
  th: ProseElement;
  td: ProseElement;
  /** Only present when the caller supplies a `linkComponent`. */
  a?: ProseElement;
};

/**
 * Heading classes: two visual tiers across the six markdown levels.
 *
 * SECTION (h1, h2) — Exposure semibold, 24/20px, tight leading. The answer's
 *   top-level divisions, and the only display type left in an answer.
 * LABEL (h3-h6) — UI sans, semibold, at BODY size with normal leading. A
 *   lead-in to the block beneath it, not a heading. `leading-tight` at body
 *   size makes a wrapped two-line label look like a rendering fault.
 *
 * Three tiers was the previous shape and it failed: section and subsection
 * were both Exposure at normal weight, separated only by size — two pixels
 * apart in compact. Once `response-headings` began keying tiers off outline
 * depth, those two became the tiers in nearly every answer, so the least
 * legible distinction carried almost all the work while the one tier with real
 * contrast sat at a depth these answers rarely reach. Two tiers differ by
 * FAMILY, so no size comparison is needed to tell them apart.
 *
 * Section is `font-semibold` so an inline `<strong>` (500) cannot outweigh it;
 * Exposure grades only read at 600. See docs/response-rendering/README.md.
 *
 * `!font-sans` on the label tier is load-bearing. `globals.css` applies
 * `font-exposure` to `:is(h1, h2, h3)` at specificity (0,1,1), so a plain
 * utility loses and an h3 would render as a display heading while its class
 * list claimed otherwise. `cva` concatenates rather than merges, so the `!` is
 * what decides this rather than stylesheet order.
 *
 * `!mb-0` in both densities: a heading's bottom gap is owned by whatever
 * follows it, via the adjacent-sibling rules in `responseProseClass`. That is
 * what lets heading-to-body sit tight while heading-to-heading keeps its own
 * rhythm. The `level` variant carries the marker, typeface and weight; size
 * comes from `DENSITY_TYPE` and margin from `DENSITY_SPACING`.
 *
 * The tiers are deliberately NOT collapsed onto fewer emitted levels:
 * `response-headings` emits a contiguous h2 -> h3 -> h4 because heading
 * navigation reads the outline, and two tiers sharing a look is presentation.
 */
// `md-h*` are markers, not styling: the adjacent-sibling rules in
// `responseProseClass` need a stable hook to select on, and Streamdown renders
// the heading element itself, so there is no wrapper to put a data attribute on.
/* eslint-disable-next-line tailwindcss/no-custom-classname -- md-h* are spacing markers, see above */
const proseHeading = cva(
  '!mb-0 break-words tracking-tight text-text-primary first:!mt-0',
  {
    variants: {
      level: {
        h1: 'md-h1 font-exposure font-semibold leading-tight',
        h2: 'md-h2 font-exposure font-semibold leading-tight',
        h3: 'md-h3 !font-sans font-semibold leading-normal',
        h4: 'md-h4 !font-sans font-semibold leading-normal',
        h5: 'md-h5 !font-sans font-semibold leading-normal',
        h6: 'md-h6 !font-sans font-semibold leading-normal',
      },
      density: {
        comfortable: '',
        compact: '',
      },
    },
    compoundVariants: DENSITIES.flatMap((density) =>
      HEADING_LEVELS.map((level) => ({
        density,
        level,
        class: `${DENSITY_TYPE[density][level]} ${DENSITY_SPACING[density][level]}`,
      }))
    ),
  }
);

/**
 * Body copy at 1.5 line height. Size comes from the density (16px on a reading
 * surface, 14px in a reference panel); the top margin varies by density and by
 * whether the block is a paragraph or a list — a list deliberately hugs the
 * block above it, so it reads as belonging to it.
 */
const proseBody = cva(
  'break-words font-normal leading-normal text-text-primary first:!mt-0',
  {
    variants: {
      density: { comfortable: '', compact: '' },
      block: { paragraph: '', list: '' },
    },
    compoundVariants: DENSITIES.flatMap((density) => [
      {
        density,
        block: 'paragraph' as const,
        class: `${DENSITY_TYPE[density].body} ${DENSITY_SPACING[density].paragraph}`,
      },
      {
        density,
        block: 'list' as const,
        class: `${DENSITY_TYPE[density].body} ${DENSITY_SPACING[density].list}`,
      },
    ]),
  }
);

/**
 * Body-text classes for a response paragraph. Exported so a consumer that must
 * swap the `<p>` for another element — a paragraph hosting block content, which
 * cannot legally nest inside `<p>` — stays in lockstep with this styling.
 */
export const responseParagraphClasses = (density: ProseDensity): string =>
  proseBody({ density, block: 'paragraph' });

/**
 * Heading classes for a level at a density. Exported so the thread scale can
 * assert against the prose scale: the rung ordering in `response-thread` is
 * only meaningful if a section heading inside an answer opens less air than
 * the boundary between a question and its answer, and that invariant spans
 * both modules. See `response-thread.test.ts`.
 */
export const responseHeadingClasses = (
  level: ProseHeadingLevel,
  density: ProseDensity
): string => proseHeading({ level, density });

/**
 * The relational half of the spacing system: gaps that depend on what a block
 * FOLLOWS, which no per-element utility can express. Goes on Streamdown's own
 * `className` so the markdown blocks are direct children of the selector root —
 * one level, no `>div>` chain to out-specify.
 *
 * `space-y-0` replaces Streamdown's `space-y-4`, so the 16px the library inserts
 * between every sibling stops competing with the rhythm below.
 *
 * Spelled out as literals rather than composed from `DENSITY_SPACING`: Tailwind
 * generates utilities by scanning source text, so a selector assembled at
 * runtime produces no CSS at all.
 *
 * Two relational rules, applied across the full cross-product of the six
 * levels:
 *
 *   - **heading after heading** hugs. The pair is one stacked title, so the
 *     gap is smaller than any block gap — the lower heading's own top margin
 *     would otherwise open a section break between a title and its own
 *     subtitle.
 *   - **heading to body** opens LESS than the heading's own top margin, so the
 *     heading groups with the block under it rather than floating between two.
 *     That asymmetry is the grouping guarantee, not a comparison against
 *     paragraph-to-paragraph: the section tier sits at the p-to-p value and
 *     still reads as owning what follows, because 32px above against 16px
 *     below is unambiguous. The label tier is tighter: at body size it is a
 *     lead-in to the very next block, not a section opener.
 *
 *     The section tier takes MORE room below than the label tier, and that is
 *     the point. It is the only tier whose size moved away from body (24px
 *     against 16px), and a 12px gap under 24px Exposure reads tighter than the
 *     same 12px under a body-size label — the larger the type, the more space
 *     it needs below to look equally separated. Optical, not arithmetic.
 *
 * h3/h4/h5/h6 take identical values throughout — they are ONE tier and render
 * identically, and the model picks between them arbitrarily. They used to
 * split, h3 on the subsection value and h4-h6 on the label value, which was a
 * leftover from the three-tier scale: after the collapse two headings that look
 * exactly alike opened different gaps, and since normalization emits h3 at
 * depth 1 and h4 at depth 2+ both are common, so the mismatch was visible in
 * ordinary answers. Same reason `h2+h3` hugs like `h1+h2` — with h3 a label,
 * that pair is a stacked title, not a section boundary.
 *
 * Both densities therefore carry ONE structure: the section tier opens at the
 * paragraph rhythm (16 / 12) and the label tier one step tighter (12 / 8).
 *
 * For the prose blocks in `DENSITY_SPACING`, the heading's tier decides that
 * gap — never the block underneath. Code blocks are framed separately in
 * `streamdown.css`. See docs/response-rendering/README.md.
 */
const COMFORTABLE_PROSE_CLASS =
  'space-y-0 ' +
  // Heading -> heading. Bottom margins are zeroed on the heading itself, so
  // these rules own the whole gap between a stacked pair.
  '[&_.md-h1+.md-h2]:!mt-3 [&_.md-h1+.md-h3]:!mt-3 [&_.md-h1+.md-h4]:!mt-3 [&_.md-h1+.md-h5]:!mt-3 [&_.md-h1+.md-h6]:!mt-3 ' +
  '[&_.md-h2+.md-h3]:!mt-3 [&_.md-h2+.md-h4]:!mt-3 [&_.md-h2+.md-h5]:!mt-3 [&_.md-h2+.md-h6]:!mt-3 ' +
  '[&_.md-h3+.md-h4]:!mt-3 [&_.md-h3+.md-h5]:!mt-3 [&_.md-h3+.md-h6]:!mt-3 ' +
  '[&_.md-h4+.md-h5]:!mt-3 [&_.md-h4+.md-h6]:!mt-3 [&_.md-h5+.md-h6]:!mt-3 ' +
  // Gap below a heading depends only on its tier (section vs label), for the
  // prose blocks in `DENSITY_SPACING`; code blocks are styled in streamdown.css.
  // `:where()` keeps specificity at (0,2,0) so the callout reset
  // `[&_.callout-block_p]:!mt-0` at (0,2,1) still wins inside a callout.
  '[&_:is(.md-h1,.md-h2)+:where(p,ul,ol,blockquote,hr,.md-table)]:!mt-4 ' +
  '[&_:is(.md-h3,.md-h4,.md-h5,.md-h6)+:where(p,ul,ol,blockquote,hr,.md-table)]:!mt-3 ' +
  // Callouts are the one deliberate exception to that: a callout is its own
  // section wherever it lands, so it keeps its two-sided margin below a heading
  // too. It resets the body margins inside the card so its internal spacing
  // survives.
  '[&_.callout-block]:!my-6 [&>.callout-block:first-child]:!mt-0 ' +
  '[&_.callout-block_p]:!mt-0 [&_.callout-block_ul]:!mt-0 [&_.callout-block_ol]:!mt-0';

const COMPACT_PROSE_CLASS =
  'space-y-0 ' +
  '[&_.md-h1+.md-h2]:!mt-2 [&_.md-h1+.md-h3]:!mt-2 [&_.md-h1+.md-h4]:!mt-2 [&_.md-h1+.md-h5]:!mt-2 [&_.md-h1+.md-h6]:!mt-2 ' +
  '[&_.md-h2+.md-h3]:!mt-2 [&_.md-h2+.md-h4]:!mt-2 [&_.md-h2+.md-h5]:!mt-2 [&_.md-h2+.md-h6]:!mt-2 ' +
  '[&_.md-h3+.md-h4]:!mt-2 [&_.md-h3+.md-h5]:!mt-2 [&_.md-h3+.md-h6]:!mt-2 ' +
  '[&_.md-h4+.md-h5]:!mt-2 [&_.md-h4+.md-h6]:!mt-2 [&_.md-h5+.md-h6]:!mt-2 ' +
  // Gap below a heading depends only on its tier; `:where()` keeps callout
  // resets winning. See the comfortable rules above.
  '[&_:is(.md-h1,.md-h2)+:where(p,ul,ol,blockquote,hr,.md-table)]:!mt-3 ' +
  '[&_:is(.md-h3,.md-h4,.md-h5,.md-h6)+:where(p,ul,ol,blockquote,hr,.md-table)]:!mt-2 ' +
  '[&_.callout-block]:!my-4 [&>.callout-block:first-child]:!mt-0 ' +
  '[&_.callout-block_p]:!mt-0 [&_.callout-block_ul]:!mt-0 [&_.callout-block_ol]:!mt-0';

export const responseProseClass = (density: ProseDensity): string =>
  density === 'compact' ? COMPACT_PROSE_CLASS : COMFORTABLE_PROSE_CLASS;

/**
 * Build the Streamdown `components` map for assistant prose at a given density.
 *
 * `a` is deliberately absent: link behaviour is surface-specific (Evidence
 * tracks source clicks and intercepts internal settings routes; Work styles a
 * plain anchor), so a caller either supplies `linkComponent` or styles anchors
 * from its own wrapper. Code rendering is delegated to the `@streamdown/code`
 * plugin and the `[data-streamdown="inline-code"]` / `"code-block"` rules in
 * `globals.css`.
 *
 * `dir="auto"` on every block: the content is unlocalized LLM markdown, so each
 * block resolves its own direction from its first strong character instead of
 * inheriting the UI direction. Without it, English content in an RTL locale gets
 * RTL paragraph direction and the bidi algorithm mirrors neutral characters
 * (`≥126 mg/dL…` renders as `mg/dL… 126 ≤`). No-op in LTR locales.
 */
export function createResponseProse({
  density = 'comfortable',
  linkComponent,
  tintedCell = false,
  headerNoWrap = false,
}: {
  density?: ProseDensity;
  linkComponent?: LinkComponent;
  /** Tint body cells so a table separates from the page background. */
  tintedCell?: boolean;
  /**
   * Keep table headers on one line so a wide table scrolls inside its clamp
   * rather than squeezing its columns — for measured surfaces like the Work
   * timeline, where a table must not resize its container as it streams in.
   */
  headerNoWrap?: boolean;
} = {}): ResponseProseComponents {
  const spacing = DENSITY_SPACING[density];
  const bodyTextClass = DENSITY_TYPE[density].body;
  const paragraphClass = proseBody({ density, block: 'paragraph' });
  const listClass = proseBody({ density, block: 'list' });

  const components: ResponseProseComponents = {
    p: ({ children }: MdComponentProps) => (
      <p dir="auto" className={paragraphClass}>
        {children}
      </p>
    ),
    h1: ({ children }: MdComponentProps) => (
      <h1 dir="auto" className={proseHeading({ level: 'h1', density })}>
        {children}
      </h1>
    ),
    h2: ({ children }: MdComponentProps) => (
      <h2 dir="auto" className={proseHeading({ level: 'h2', density })}>
        {children}
      </h2>
    ),
    h3: ({ children }: MdComponentProps) => (
      <h3 dir="auto" className={proseHeading({ level: 'h3', density })}>
        {children}
      </h3>
    ),
    h4: ({ children }: MdComponentProps) => (
      <h4 dir="auto" className={proseHeading({ level: 'h4', density })}>
        {children}
      </h4>
    ),
    h5: ({ children }: MdComponentProps) => (
      <h5 dir="auto" className={proseHeading({ level: 'h5', density })}>
        {children}
      </h5>
    ),
    h6: ({ children }: MdComponentProps) => (
      <h6 dir="auto" className={proseHeading({ level: 'h6', density })}>
        {children}
      </h6>
    ),
    ul: ({ children }: MdComponentProps) => (
      <ul dir="auto" className={cn('list-disc ps-6', listClass)}>
        {children}
      </ul>
    ),
    ol: ({ children }: MdComponentProps) => (
      <ol dir="auto" className={cn('list-decimal ps-6', listClass)}>
        {children}
      </ol>
    ),
    li: ({ children }: MdComponentProps) => (
      <li
        dir="auto"
        className={cn(
          'break-words font-normal leading-normal first:!mt-0',
          bodyTextClass,
          spacing.listItem
        )}
      >
        {children}
      </li>
    ),
    // A soft step above body — clear enough to read as emphasis, light enough
    // not to compete with the heading tier. Matches the callout `<strong>` so
    // inline bolding is consistent inside and outside a card.
    strong: ({ children }: MdComponentProps) => (
      <strong className="font-medium">{children}</strong>
    ),
    em: ({ children }: MdComponentProps) => <em>{children}</em>,
    blockquote: ({ children }: MdComponentProps) => (
      <blockquote
        dir="auto"
        className={cn(
          'break-words border-s-4 border-outline ps-4 font-normal italic leading-normal text-text-primary first:!mt-0',
          bodyTextClass,
          spacing.blockquote
        )}
      >
        {children}
      </blockquote>
    ),
    hr: () => <hr className={cn('border-outline first:!mt-0', spacing.hr)} />,
    ...createTableMdComponents({
      // `md-table` is a marker for surfaces that need to find a rendered table,
      // not a styling class.
      /* eslint-disable-next-line tailwindcss/no-custom-classname -- md-table is a marker, see above */
      wrapperClassName: cn(
        'md-table grid grid-cols-1 overflow-hidden rounded-md border first:!mt-0',
        spacing.table
      ),
      cellTextClass: bodyTextClass,
      tintedCell,
      headerNoWrap,
    }),
  };

  if (linkComponent) {
    components.a = linkComponent as ProseElement;
  }

  return components;
}
